import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }
`;

const colorCycle = keyframes`
  0%, 100% { color: var(--slate); }
  20% { color: var(--green); }
  40% { color: var(--slate); }
`;

const StyledInlineCode = styled.code`
  font-family: var(--font-mono);
  font-size: 0.75em;
  color: var(--green);
  vertical-align: middle;
`;

const StyledEraLetter = styled.span`
  display: inline-block;
  animation: ${colorCycle} 1.6s ease infinite;
  animation-delay: ${({ delay }) => delay};
  color: var(--slate);
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>Aloha, my name is</h1>;
  const two = <h2 className="big-heading">chaz kondo</h2>;
  const three = (
    <h3 className="big-heading">
      let's <StyledInlineCode>code</StyledInlineCode> the new{' '}
      <span style={{ whiteSpace: 'nowrap' }}>
        <StyledEraLetter delay="400ms">e</StyledEraLetter>
        <StyledEraLetter delay="550ms">r</StyledEraLetter>
        <StyledEraLetter delay="700ms">a</StyledEraLetter>
        <StyledEraLetter delay="850ms">.</StyledEraLetter>
      </span>
    </h3>
  );
  const four = (
    <>
      <p>
        I'm a software engineer committed to building the future of Hawai
        <span style={{ fontSize: '17px' }}>ʻ</span>i’s tech
        <span style={{ fontSize: '12px' }}> x </span>cultural landscape. Currently, I'm working on
        expanding automated AI projects and implementing secure &amp; scalable solutions at{' '}
        <a href="https://www.boozallen.com/" target="_blank" rel="noreferrer">
          Booz Allen
        </a>
        .
      </p>
    </>
  );
  const five = (
    <a
      className="email-link"
      href="https://www.linkedin.com/in/chazkondo"
      target="_blank"
      rel="noreferrer">
      Let's Connect
    </a>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
